import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { environment } from '../../../../environments/environement';
import { Router } from '@angular/router';

export interface EtablissementDTO {
  id?: number;
  nom: string;
  adresse: string;
  telephone: string;
  email: string;
  archived?: boolean;
  logoUrl?: string;
  primaryColor?: string;
  secodaryColor?: string;
  bgColor?: string;
  bsubmitColor?: string;
  beditColor?: string;
  bexitColor?: string;
  titleColor?: string;
  primaryTextColor?: string;
  secodaryTextColor?: string;
  avatarPlaceholderUrl?: string;
  editIconUrl?: string;
  addIconUrl?: string;
  deleteIconUrl?: string;
  cancelIconUrl?: string;
  logoFile?: File | null;
  createdAt?: string;
  updatedAt?: string;
}

export interface UserDTO {
  userId?: number;
  nom: string;
  prenom: string;
  userEmail: string;
  userPhoneNumber?: string;
  role?: string;
  roles?: string[];
  archived?: boolean;
  etablissementId?: number;
  etablissementNom?: string;
  photoUrl?: string;
  adresse?: string;
  createdAt?: string;
}

export interface SuperAdminStats {
  totalEtablissements: number;
  activeEtablissements: number;
  archivedEtablissements: number;
  totalUsers: number;
  totalDirecteurs: number;
  totalFormateurs: number;
  totalParents: number;
  perEtablissement: {
    id: number;
    nom: string;
    color: string;
    userCount: number;
    parentCount: number;
    formateurCount: number;
  }[];
}

export interface SuperAdminLoginResponse {
  accessToken: string;
  role: string;
  name: string;
}

export interface CreateUserDTO {
  nom: string;
  prenom: string;
  userEmail: string;
  userPassword: string;
  userRole: 'Directeur' | 'Formateur' | 'Parent';
  userPhoneNumber?: string;
  cinParent?: string;
  sexe?: string;
  Adresse?: string;
  Specialite?: string;
  etablissementId: number;
}

export interface AppVersionDTO {
  id?: number;
  latestVersion: string;
  forceUpdate: boolean;
  updateUrlAndroid: string;
  updateUrlIos: string;
  createdAt?: string;
}

export interface ConfigCongeDTO {
  id?: number;
  etablissementId: number;
  congePayeMensuel: number;
  congeMariageParAn: number;
  congeMaladieParAn: number;
  congeMaladieParSemestre: number;
  congeMaterniteMois: number;
}

export interface CsvImportResult {
  success: number;
  errors: { row: number; message: string }[];
  total: number;
}

export interface ContactMessageDTO {
  id?: number;
  nom: string;
  email: string;
  sujet: string;
  message: string;
  reponse?: string;
  dateReponse?: string;
  dateEnvoi?: string;
  lu?: boolean;
}

export interface StandardResponseDTO {
  id?: number;
  titre: string;
  contenu: string;
  dateCreation?: string;
}

@Injectable({ providedIn: 'root' })
export class SuperAdminService {
  private baseUrl = environment.apiUrl;

  constructor(private http: HttpClient, private router: Router) {}

  // ===== AUTH =====
  login(email: string, password: string): Observable<SuperAdminLoginResponse> {
    return this.http.post<SuperAdminLoginResponse>(`${this.baseUrl}/auth/signin`, {
      userEmail: email,
      userPassword: password
    }).pipe(
      tap((res: any) => {
        const roles: string[] = res.roles || [];
        if (res.accessToken && roles.includes('Administareur')) {
          localStorage.setItem('sa_presence', res.accessToken);
          localStorage.setItem('sa_role', 'Administareur');
          localStorage.setItem('sa_name', res.name || res.nom || email);
        }
      })
    );
  }

  logout(): void {
    localStorage.removeItem('sa_presence');
    localStorage.removeItem('sa_role');
    localStorage.removeItem('sa_name');
    this.router.navigate(['/super-admin/login']);
  }

  isSuperAdmin(): boolean {
    return localStorage.getItem('sa_role') === 'Administareur' &&
           !!localStorage.getItem('sa_presence');
  }

  private getAuthHeaders(): HttpHeaders {
    const token = localStorage.getItem('sa_presence');
    return token
      ? new HttpHeaders({ Authorization: `Bearer ${token}` })
      : new HttpHeaders();
  }

  // ===== ETABLISSEMENTS CRUD =====
  getAllEtablissements(): Observable<EtablissementDTO[]> {
    return this.http.get<EtablissementDTO[]>(`${this.baseUrl}/api/v1/etablissements`, {
      headers: this.getAuthHeaders()
    });
  }

  getEtablissementById(id: number): Observable<EtablissementDTO> {
    return this.http.get<EtablissementDTO>(`${this.baseUrl}/api/v1/etablissements/${id}`, {
      headers: this.getAuthHeaders()
    });
  }

  createEtablissement(data: EtablissementDTO): Observable<EtablissementDTO> {
    const fd = this.toFormData(data, 'files');
    return this.http.post<EtablissementDTO>(`${this.baseUrl}/api/v1/etablissements`, fd, {
      headers: this.getAuthHeaders()
    });
  }

  updateEtablissement(id: number, data: EtablissementDTO): Observable<EtablissementDTO> {
    const fd = this.toFormData(data, 'logoFile');
    return this.http.put<EtablissementDTO>(`${this.baseUrl}/api/v1/etablissements/${id}`, fd, {
      headers: this.getAuthHeaders()
    });
  }

  private toFormData(data: EtablissementDTO, fileFieldName: string = 'files'): FormData {
    const fd = new FormData();
    const textFields: (keyof EtablissementDTO)[] = [
      'nom', 'adresse', 'telephone', 'email',
      'primaryColor', 'secodaryColor', 'bgColor',
      'bsubmitColor', 'beditColor', 'bexitColor',
      'titleColor', 'primaryTextColor', 'secodaryTextColor',
      'avatarPlaceholderUrl', 'editIconUrl', 'addIconUrl', 'deleteIconUrl', 'cancelIconUrl'
    ];
    textFields.forEach(key => {
      const val = data[key];
      if (val !== null && val !== undefined && typeof val === 'string') {
        fd.append(key as string, val);
      }
    });
    if (data.logoFile instanceof File) {
      fd.append(fileFieldName, data.logoFile, data.logoFile.name);
    }
    return fd;
  }

  deleteEtablissement(id: number): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/api/v1/etablissements/${id}`, {
      headers: this.getAuthHeaders()
    });
  }

  archiveEtablissement(id: number): Observable<void> {
    return this.http.put<void>(`${this.baseUrl}/api/v1/etablissements/${id}/archive`, {}, {
      headers: this.getAuthHeaders()
    });
  }

  unarchiveEtablissement(id: number): Observable<void> {
    return this.http.put<void>(`${this.baseUrl}/api/v1/etablissements/${id}/unarchive`, {}, {
      headers: this.getAuthHeaders()
    });
  }

  // ===== USERS =====
  getAllUsersByEtablissement(etablissementId: number): Observable<UserDTO[]> {
    return this.http.get<UserDTO[]>(
      `${this.baseUrl}/users/non-archived/by-etablissement/${etablissementId}`,
      { headers: this.getAuthHeaders() }
    );
  }

  getAllArchivedUsersByEtablissement(etablissementId: number): Observable<UserDTO[]> {
    return this.http.get<UserDTO[]>(
      `${this.baseUrl}/users/archived/by-etablissement/${etablissementId}`,
      { headers: this.getAuthHeaders() }
    );
  }

  archiveUser(userId: number): Observable<void> {
    return this.http.put<void>(`${this.baseUrl}/users/${userId}/archive`, {}, {
      headers: this.getAuthHeaders()
    });
  }

  unarchiveUser(userId: number): Observable<void> {
    return this.http.put<void>(`${this.baseUrl}/users/${userId}/unarchive`, {}, {
      headers: this.getAuthHeaders()
    });
  }

  deleteUser(userId: number): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/users/delete/${userId}`, {
      headers: this.getAuthHeaders()
    });
  }

  getUsersCountByEtablissement(etablissementId: number): Observable<number> {
    return this.http.get<number>(
      `${this.baseUrl}/users/NombresUtilisateurs?etablissementId=${etablissementId}`,
      { headers: this.getAuthHeaders() }
    );
  }

  getParentsCountByEtablissement(etablissementId: number): Observable<number> {
    return this.http.get<number>(
      `${this.baseUrl}/users/NombresUtilisateurs/parents?etablissementId=${etablissementId}`,
      { headers: this.getAuthHeaders() }
    );
  }

  getFormateursCountByEtablissement(etablissementId: number): Observable<number> {
    return this.http.get<number>(
      `${this.baseUrl}/users/NombresUtilisateurs/formateurs?etablissementId=${etablissementId}`,
      { headers: this.getAuthHeaders() }
    );
  }

  // ===== USER CREATION =====
  createUser(payload: CreateUserDTO): Observable<any> {
    return this.http.post<any>(`${this.baseUrl}/auth/signup`, payload, {
      headers: this.getAuthHeaders()
    });
  }

  // ===== APP VERSION =====
  getLatestVersion(): Observable<AppVersionDTO> {
    return this.http.get<AppVersionDTO>(`${this.baseUrl}/api/v1/app-version/check-update`);
  }

  getAllVersions(): Observable<AppVersionDTO[]> {
    return this.http.get<AppVersionDTO[]>(`${this.baseUrl}/api/v1/app-version/all`, {
      headers: this.getAuthHeaders()
    });
  }

  publishVersion(data: AppVersionDTO): Observable<AppVersionDTO> {
    const payload = {
      latestVersion: data.latestVersion,
      forceUpdate: data.forceUpdate,
      updateUrlAndroid: data.updateUrlAndroid,
      updateUrlIos: data.updateUrlIos
    };
    return this.http.post<AppVersionDTO>(`${this.baseUrl}/api/v1/app-version`, payload, {
      headers: this.getAuthHeaders()
    });
  }

  // ===== CONFIG CONGE =====
  getConfigCongeByEtablissement(etablissementId: number): Observable<ConfigCongeDTO> {
    return this.http.get<ConfigCongeDTO>(
      `${this.baseUrl}/api/v1/config-conge/etablissement/${etablissementId}`,
      { headers: this.getAuthHeaders() }
    );
  }

  saveConfigConge(data: ConfigCongeDTO): Observable<ConfigCongeDTO> {
    return this.http.post<ConfigCongeDTO>(`${this.baseUrl}/api/v1/config-conge`, data, {
      headers: this.getAuthHeaders()
    });
  }

  // ===== CONTACT FORM =====
  sendContactMessage(data: { nom: string; email: string; sujet: string; message: string }): Observable<any> {
    return this.http.post(`${this.baseUrl}/api/contact`, data);
  }

  getContactMessages(): Observable<ContactMessageDTO[]> {
    return this.http.get<ContactMessageDTO[]>(`${this.baseUrl}/api/contact/admin`, {
      headers: this.getAuthHeaders()
    });
  }

  getContactMessage(id: number): Observable<ContactMessageDTO> {
    return this.http.get<ContactMessageDTO>(`${this.baseUrl}/api/contact/admin/${id}`, {
      headers: this.getAuthHeaders()
    });
  }

  getContactUnreadCount(): Observable<{ count: number }> {
    return this.http.get<{ count: number }>(`${this.baseUrl}/api/contact/admin/unread-count`, {
      headers: this.getAuthHeaders()
    });
  }

  replyContactMessage(id: number, reponse: string): Observable<any> {
    return this.http.put(`${this.baseUrl}/api/contact/admin/${id}/reply`, { reponse }, {
      headers: this.getAuthHeaders()
    });
  }

  deleteContactMessage(id: number): Observable<any> {
    return this.http.delete(`${this.baseUrl}/api/contact/admin/${id}`, {
      headers: this.getAuthHeaders()
    });
  }

  markAllContactRead(): Observable<any> {
    return this.http.put(`${this.baseUrl}/api/contact/admin/mark-all-read`, {}, {
      headers: this.getAuthHeaders()
    });
  }

  deleteAllContactMessages(): Observable<any> {
    return this.http.delete(`${this.baseUrl}/api/contact/admin/delete-all`, {
      headers: this.getAuthHeaders()
    });
  }

  // ===== STANDARD RESPONSES =====
  getStandardResponses(): Observable<StandardResponseDTO[]> {
    return this.http.get<StandardResponseDTO[]>(`${this.baseUrl}/api/contact/admin/standard-reponses`, {
      headers: this.getAuthHeaders()
    });
  }

  createStandardResponse(data: StandardResponseDTO): Observable<any> {
    return this.http.post(`${this.baseUrl}/api/contact/admin/standard-reponses`, data, {
      headers: this.getAuthHeaders()
    });
  }

  updateStandardResponse(id: number, data: StandardResponseDTO): Observable<any> {
    return this.http.put(`${this.baseUrl}/api/contact/admin/standard-reponses/${id}`, data, {
      headers: this.getAuthHeaders()
    });
  }

  deleteStandardResponse(id: number): Observable<any> {
    return this.http.delete(`${this.baseUrl}/api/contact/admin/standard-reponses/${id}`, {
      headers: this.getAuthHeaders()
    });
  }

  // ===== PASSWORD GENERATOR =====
  generatePassword(length: number = 12): string {
    const upper   = 'ABCDEFGHJKLMNPQRSTUVWXYZ';
    const lower   = 'abcdefghjkmnpqrstuvwxyz';
    const digits  = '23456789';
    const special = '@#$!%*?&';
    const all     = upper + lower + digits + special;
    let pwd = '';
    pwd += upper[Math.floor(Math.random() * upper.length)];
    pwd += lower[Math.floor(Math.random() * lower.length)];
    pwd += digits[Math.floor(Math.random() * digits.length)];
    pwd += special[Math.floor(Math.random() * special.length)];
    for (let i = 4; i < length; i++) {
      pwd += all[Math.floor(Math.random() * all.length)];
    }
    return pwd.split('').sort(() => Math.random() - 0.5).join('');
  }
}
